import { EVENTS } from '@bs/constants';

/**
 * The BarChart module which handles animated bar chart visualization.
 * @class BarChart
 */
export default class BarChart {
  /**
   * BarChart constructor which assigns the element passed into the constructor
   * to the `this.element` property for later reference
   * @param {HTMLElement} element - REQUIRED - the module's container
   */
  constructor(element) {
    /**
     * Reference to the DOM element that is the root of the component
     * @property {object} element - The DOM element that serves as the root of the component.
     */
    this.element = element;

    // Config
    this.maxDataValue = 100;
    this.maxHeight = 163;

    // Initialize the view
    this.init();
  }

  /**
   * Initializes the view by calling the functions to
   * create DOM references, setup event handlers and
   * then create the event listeners
   * @returns {object} BarChart A reference to the current instance of the class
   * @chainable
   */
  init() {
    this.cacheDomReferences().setupHandlers().enable();

    return this;
  }

  /**
   * Find all necessary DOM elements used in the view and cache them
   * @returns {object} BarChart A reference to the instance of the class
   * @chainable
   */
  cacheDomReferences() {
    this.fill = this.element.querySelector('.bar__fill');

    // Check if required elements exist
    if (!this.fill) {
      // eslint-disable-next-line no-console
      return this;
    }

    return this;
  }

  /**
   * Bind event handlers with the proper context of `this`.
   * @returns {object} A reference to the current instance of the class
   * @chainable
   */
  setupHandlers() {
    this.animateBar = this.animateBar.bind(this);
    return this;
  }

  /**
   * Create event handlers to enable interaction with view
   * @returns {object} BarChart A reference to the instance of the class
   * @chainable
   */
  enable() {
    if (this.fill) {
      this.animateBar();
    }
    return this;
  }

  /**
   * Animate the bar chart by increasing its height
   * @returns {object} A reference to the current instance of the class
   */
  animateBar() {
    if (!this.fill) return this;

    const dataValue = this.element.getAttribute('data-value');
    if (!dataValue) {
      // eslint-disable-next-line no-console
      console.warn('BarChart: data-value attribute not found on bar element');
      return this;
    }

    const numericValue = parseInt(dataValue, 10);
    if (Number.isNaN(numericValue) || numericValue < 0) {
      // eslint-disable-next-line no-console
      console.warn(
        'BarChart: Invalid data-value. Must be a positive number:',
        dataValue
      );
      return this;
    }

    const height = (numericValue / this.maxDataValue) * this.maxHeight;

    // Animate bar
    setTimeout(() => {
      this.fill.style.height = `${height}px`;
    }, 100);

    return this;
  }
}

/**
 * Initialize all bar charts on the page
 */
export function initBarCharts() {
  const bars = document.querySelectorAll('.bar');

  if (!bars.length) return;

  const barObserver = new IntersectionObserver(
    (entries, observer) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          // eslint-disable-next-line no-new
          new BarChart(entry.target);
          observer.unobserve(entry.target); // Run once per bar
        }
      });
    },
    { threshold: 0.3 }
  );

  bars.forEach((bar) => barObserver.observe(bar));
}

// Auto-initialize when DOM is ready
document.addEventListener(EVENTS.DOM_CONTENT_LOADED, initBarCharts);
